import React, { useState, useEffect } from "react";
import PageLayout from "../components/PageLayout";
import { FaRegBuilding, FaChevronRight, FaExternalLinkAlt } from "react-icons/fa";
import { RiMedicineBottleLine, RiLeafLine, RiFileListLine, RiInformationLine, RiCheckLine } from "react-icons/ri";
import { HiOutlineBeaker } from "react-icons/hi";
import { MdCategory, MdOutlineScience, MdOutlineWarning, MdCompareArrows } from "react-icons/md";
import { TbPill, TbDroplet } from "react-icons/tb";
import { Link, useParams } from "react-router-dom";
import TradenameAPI from "../api/tradename";
import SponsersAffiliations from "../components/SponsersAffiliations";
import TopBrandBadge from "../components/commons/TopBrandBadge";
import { useSchemaMicrodata } from '../hooks/useSchemaMicrodata';
import { useCompare } from '../hooks/useCompare';
import RelatedTradenames from '../components/tradenames/RelatedTradenames';

export default function TradeNameItem() {
    const { uri } = useParams();
    const [product, setProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSection, setActiveSection] = useState("overview");
    const { isSelected, toggleItem, isFull } = useCompare();

    // Add schema microdata for product page
    useSchemaMicrodata('product', {
      name: product?.name,
      description: product?.description,
      ingredients: product?.ingredients,
      manufacturer: product?.manufacturers?.[0]
    }, [product]);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setIsLoading(true);
                const data = await TradenameAPI.getOne(uri);
                setProduct(data);
            } catch (err) {
                setError('Failed to load product details. Please try again later.');
                console.error('Error fetching product:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProduct();
    }, [uri]);

    const sections = [
        { id: 'overview', label: 'Overview', icon: RiMedicineBottleLine },
        { id: 'ingredients', label: 'Ingredients', icon: RiLeafLine },
        { id: 'composition', label: 'Composition', icon: HiOutlineBeaker },
        { id: 'classifications', label: 'Classifications', icon: MdCategory }
    ];

    if (isLoading) {
        return (
            <PageLayout
                title=" "
                titleHighlight="Loading..."
                subtitle="Please wait while we fetch the product details"
                pageTitle="Loading"
                showSearch={false}
                showGradient={true}
            >
                <div className="flex items-center justify-center h-64">
                    <div className="w-12 h-12 border-t-2 border-b-2 rounded-full animate-spin border-primary"></div>
                </div>
            </PageLayout>
        );
    }

    if (error || !product) {
        return (
            <PageLayout
                title=" "
                titleHighlight="Error"
                subtitle="We couldn't load this medicine"
                pageTitle="Error"
                showSearch={true}
                showGradient={true}
            >
                <div className="p-8 text-center border shadow-sm bg-white rounded-xl border-secondary/8 max-w-lg mx-auto mt-8">
                    <div className="flex items-center justify-center size-14 rounded-2xl bg-red-50 mx-auto mb-4">
                        <span className="text-red-500 text-24">!</span>
                    </div>
                    <h3 className="text-20 font-semibold text-secondary mb-2">
                        Something went wrong
                    </h3>
                    <p className="text-16 font-text text-secondary/80 mb-6 leading-relaxed">
                        {error || 'We couldn\'t find this product. It may have been removed or the link might be incorrect.'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            onClick={() => window.location.reload()}
                            className="px-5 py-3 text-16 font-text font-medium text-white rounded-lg bg-primary hover:bg-primary/90 transition-colors min-h-[44px]"
                        >
                            Try again
                        </button>
                        <a
                            href="/"
                            className="px-5 py-3 text-16 font-text font-medium text-secondary/80 rounded-lg bg-secondary/5 hover:bg-secondary/10 transition-colors min-h-[44px]"
                        >
                            Go to homepage
                        </a>
                    </div>
                </div>
            </PageLayout>
        );
    }

    const selected = product ? isSelected(product.uri) : false;
    const compareDisabled = !selected && isFull;

    const compareButton = product && (
        <button
            onClick={() => toggleItem(product)}
            disabled={compareDisabled}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-15 font-text font-semibold rounded-xl active:scale-[0.98] transition-all duration-200 min-h-[44px] ${
                selected
                    ? 'bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow'
                    : compareDisabled
                        ? 'bg-secondary/5 border border-secondary/10 text-secondary/30 cursor-not-allowed shadow-none'
                        : 'bg-primary hover:bg-primary/90 text-white shadow-sm hover:shadow'
            }`}
        >
            {selected ? (
                <RiCheckLine className="size-5" />
            ) : (
                <MdCompareArrows className="size-5" />
            )}
            {selected ? 'Added to comparison' : 'Compare with others'}
        </button>
    );

    return (
        <PageLayout
            title=" "
            titleHighlight={product.name}
            subtitle="Pharmaceutical Information"
            pageTitle={product.name}
            showSearch={false}
            showGradient={true}
            headerActions={compareButton}
        >
            {/* Main Content - More compact layout */}
            <div className="mt-6">
                <div className="flex flex-col lg:flex-row lg:gap-6">
                    {/* Side Navigation - Only visible on mobile */}
                    <div className="w-full mb-4 md:hidden">
                        <div className="grid grid-cols-2 gap-2">
                            {sections.map((section) => (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveSection(section.id)}
                                    className={`flex items-center justify-center p-2.5 text-14 border font-text rounded-md transition-colors ${activeSection === section.id
                                        ? 'bg-primary/20 text-primary border-primary/40'
                                        : 'bg-white/40 text-secondary/90 hover:bg-white/60 border-white/40 hover:border-white/70 shadow-sm'
                                        }`}
                                >
                                    <section.icon className="mr-1.5 size-4" />
                                    {section.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Side Navigation - Desktop, more compact */}
                    <div className="hidden w-full md:block lg:w-64 shrink-0">
                        <div className="sticky top-16">
                            <div className="p-4 border rounded-lg shadow-2xl bg-white/40 border-white/60 shadow-light-gray/20">
                                <h2 className="font-semibold text-secondary text-16">Product Information</h2>
                                <nav className="mt-4 space-y-1">
                                    {sections.map((section) => (
                                        <button
                                            key={section.id}
                                            onClick={() => setActiveSection(section.id)}
                                            className={`flex items-center justify-between w-full px-3 py-2 rounded-md transition-all ${activeSection === section.id
                                                ? 'bg-primary/10 text-primary'
                                                : 'text-secondary/90 hover:bg-gray-100'
                                                }`}
                                        >
                                            <div className="flex items-center text-14 font-text">
                                                <section.icon className="mr-2 size-4" />
                                                {section.label}
                                            </div>
                                            {activeSection === section.id && (
                                                <FaChevronRight className="size-2.5" />
                                            )}
                                        </button>
                                    ))}
                                </nav>
                            </div>

                            <div className="p-4 mt-4 border rounded-lg shadow-2xl shadow-light-gray/20 bg-white/40 border-white/60">
                                <h3 className="font-semibold text-secondary text-16">Categories</h3>
                                <div className="flex flex-wrap gap-1.5 mt-3">
                                    {product.categories.map((category, idx) => (
                                        <span key={idx} className="px-2.5 py-1 text-14 font-text rounded-full text-secondary/90 bg-secondary/10">
                                            {category}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div className="p-4 mt-4 border rounded-lg shadow-2xl shadow-light-gray/20 bg-white/40 border-white/60">
                                <div className="flex items-center text-amber-600">
                                    <MdOutlineWarning className="size-4" />
                                    <h3 className="ml-2 font-medium text-16">Medical Information</h3>
                                </div>
                                <p className="mt-2 text-secondary/70 text-14 font-text">
                                    This information is for educational purposes only.
                                    Always consult a healthcare professional before use.
                                </p>
                            </div>

                        </div>
                    </div>

                    {/* Content Area - More compact cards */}
                    <div className="flex-1 mt-4 lg:mt-0">
                        {/* Overview Section */}
                        {activeSection === 'overview' && (
                            <div className="space-y-4 animate-fadeIn">
                                <div className="p-5 border rounded-lg shadow-2xl shadow-light-gray/20 bg-white/40 border-white/60">
                                    <div className="flex items-center justify-start gap-2">
                                        <div className="inline-flex items-center px-2 py-0.5 mb-2 text-14 font-text rounded-full bg-primary/10 text-primary">
                                            <RiMedicineBottleLine className="mr-1 size-3.5" />
                                            Pharmaceutical Product
                                        </div>
                                        {
                                            (product.is_sponsor || product.review_score > 4 )&& (
                                                <TopBrandBadge className="mt-0 mb-2 h-[22px]"/>
                                            )
                                        }
                                    </div>

                                    <h2 className="font-semibold text-20 text-secondary">About {product.name}</h2>
                                    <p className="mt-3 leading-relaxed text-16 font-text text-secondary/80">
                                        {product.name} is manufactured by {product.manufacturers.map(m => m.name).join(", ")} and is available for purchase through authorized pharmacies and distributors.
                                        This medication contains multiple active ingredients that work synergistically to provide therapeutic effects.
                                    </p>

                                    {/* Compare button moved to page header */}

                                    <div className="grid grid-cols-1 gap-4 mt-5 sm:grid-cols-2">
                                        <div className="p-3 transition-all border rounded-md bg-white/40 border-white/60 hover:border-primary/30">
                                            <div className="flex items-center">
                                                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10">
                                                    <FaRegBuilding className="size-4 text-primary" />
                                                </div>
                                                <h3 className="ml-2 font-medium text-secondary text-16">Manufactured by</h3>
                                            </div>
                                            <p className="pl-10 mt-2 text-gray-600 text-16 font-text">
                                                {product.manufacturers.map(m => m.name).join(", ")}
                                            </p>
                                        </div>

                                        <div className="p-3 transition-all border rounded-md bg-white/40 border-white/60 hover:border-primary/30">
                                            <div className="flex items-center">
                                                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10">
                                                    <RiLeafLine className="size-4 text-primary" />
                                                </div>
                                                <h3 className="ml-2 font-medium text-secondary text-16">Active Ingredients</h3>
                                            </div>
                                            <p className="pl-10 mt-2 text-gray-600 text-16 font-text">
                                                Contains {product.ingredients.length} active pharmaceutical ingredients
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap justify-between items-start p-5 border rounded-lg shadow-2xl shadow-light-gray/20 bg-white/40 border-white/60">
                                    <div className="w-full xl:max-w-[75%]">
                                        <p className="leading-relaxed text-16 font-text text-secondary/80">
                                            Need Reliable Access to <span className="font-bold">{product.name}</span> ?
                                            Explore our curated list of reviewed verified, licensed pharmacies at Pharmacypilot.info supporting safe, ethical access and unbiased ratings without the markup.
                                        </p>
                                    </div>

                                    <Link
                                        to={`https://pharmacypilot.info?utm_source=pharmacywiki&utm_medium=trade_name&utm_campaign=find_more_purchased_store`}
                                        target="_blank"
                                        className="w-fit  h-fit mt-4 xl:my-auto inline-flex items-center justify-center py-2 px-4 font-medium text-white transition-all rounded-md shadow-sm text-16 bg-green-500 hover:bg-green-600"
                                    >
                                        <FaExternalLinkAlt className="mr-2 size-4" />
                                        Visit Pharmacypilot.info
                                    </Link>
                                </div>

                                <div className="p-5 border rounded-lg shadow-2xl shadow-light-gray/20 bg-white/40 border-white/60">
                                    <h2 className="font-semibold text-20 text-secondary">Key Information</h2>
                                    <div className="mt-4 space-y-3">
                                        {[
                                            {
                                                label: "Pharmaceutical companies",
                                                value: product.manufacturers.map(m => m.name).join(", "),
                                                icon: FaRegBuilding
                                            },
                                            {
                                                label: "Medical categories",
                                                value: product.categories.length > 0 ? product.categories.length + " categories" : "Not specified",
                                                icon: TbPill
                                            },
                                            {
                                                label: "Available forms",
                                                value: product.forms.length > 0 ? product.forms[0][0] : "Not specified",
                                                icon: TbDroplet
                                            }
                                        ].map((item, idx) => (
                                            <div key={idx} className="flex items-start p-3 border rounded-md bg-white/20 border-white/80">
                                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                                                    <item.icon className="size-4 text-primary" />
                                                </div>
                                                <div className="ml-3">
                                                    <h3 className="font-medium text-secondary text-16">{item.label}</h3>
                                                    <p className="mt-0.5 text-14 font-text text-gray-600">{item.value}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="p-3 mt-4 border rounded-md border-amber-300/70 bg-amber-200/40">
                                        <div className="flex items-center text-amber-700">
                                            <RiInformationLine className="size-4" />
                                            <h3 className="ml-2 font-medium text-14 font-text">Important Information</h3>
                                        </div>
                                        <p className="mt-1 text-14 font-text text-amber-700">
                                            The information provided is for educational purposes only.
                                            Always consult with a healthcare professional before using this medication.
                                        </p>
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* Ingredients Section */}
                        {activeSection === 'ingredients' && (
                            <div className="space-y-4 animate-fadeIn">
                                <div className="p-5 border rounded-lg shadow-2xl shadow-light-gray/20 bg-white/40 border-white/60">
                                    <div className="inline-flex items-center px-2 py-0.5 mb-2 text-14 font-text rounded-full bg-primary/10 text-primary">
                                        <RiLeafLine className="mr-1 size-3.5" />
                                        Active Components
                                    </div>
                                    <h2 className="font-semibold text-20 text-secondary">Active Ingredients</h2>
                                    <p className="mt-3 leading-relaxed text-secondary/80 text-16">
                                        {product.name} contains {product.ingredients.length} active ingredients that work together
                                        to provide therapeutic effects.
                                    </p>
                                </div>

                                <div className="grid gap-3 sm:grid-cols-2">
                                    {product.ingredients.map((ingredient) => (
                                        <div key={ingredient.uri} className="p-4 transition-all border rounded-md shadow-sm bg-white/40 border-white/60 hover:border-primary/30">
                                            <Link
                                                to={`/ingredients/${ingredient.uri}`}
                                                className="flex items-center gap-2 text-16 text-secondary hover:text-primary"
                                            >
                                                <RiLeafLine className="size-4" />
                                                {ingredient.name}
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Composition Section */}
                        {activeSection === 'composition' && (
                            <div className="space-y-4 animate-fadeIn">
                                <div className="p-5 border rounded-lg shadow-2xl shadow-light-gray/20 bg-white/40 border-white/60">
                                    <div className="inline-flex items-center px-2 py-0.5 mb-2 text-14 font-text rounded-full bg-primary/10 text-primary">
                                        <HiOutlineBeaker className="mr-1 size-3.5" />
                                        Formula Details
                                    </div>
                                    <h2 className="font-semibold text-20 text-secondary">Composition Details</h2>
                                    <p className="mt-3 leading-relaxed text-secondary/80 text-16">
                                        The following information represents the detailed composition of {product.name}.
                                    </p>
                                </div>

                                {product.forms?.[0]?.length > 2 ? (
                                    <div className="overflow-hidden border rounded-lg shadow-sm bg-white/40 border-white/60">
                                        <div className="p-4 border-b">
                                            <h3 className="flex items-center font-medium text-secondary text-16">
                                                <MdOutlineScience className="mr-1.5 size-4 text-primary" />
                                                Dosage Information
                                            </h3>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="bg-white/60">
                                                        <th className="p-3 text-left text-14 font-medium text-secondary/90 border-b border-white/70">Forms</th>
                                                        <th className="p-3 text-left text-14 font-medium text-secondary/90 border-b border-white/70">Usage/Route Administration</th>
                                                        <th className="p-3 text-left text-14 font-medium text-secondary/90 border-b border-white/70">
                                                                Ingredients
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {product.forms.map((form, rowIdx) => (
                                                        <tr key={rowIdx} className="hover:bg-white/60 transition-all border-b border-white/70">
                                                            <td className="p-3 text-16 text-secondary/90">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex items-center justify-center w-6 h-6 text-white rounded-md bg-primary/90">
                                                                        <span className="font-medium text-12 font-text">{rowIdx + 1}</span>
                                                                    </div>
                                                                    <div>
                                                                        {form[0]}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="p-3 text-16 text-secondary/90">
                                                                {form[1]}
                                                            </td>
                                                            <td className="p-3 text-16 text-secondary/90">
                                                                <ul className="list-disc pl-5">
                                                                    {form.slice(2).map((ingredient, colIdx) => (
                                                                        <li key={`${rowIdx}-ingredient-${colIdx}`}>{ingredient}</li>
                                                                    ))}
                                                                </ul>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-6 text-center border rounded-lg shadow-sm bg-white/40 border-white/60">
                                        <RiFileListLine className="mx-auto mb-3 text-secondary/40 size-8" />
                                        <p className="text-secondary/70 text-16">No detailed composition information available.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Classifications Section */}
                        {activeSection === 'classifications' && (
                            <div className="space-y-4 animate-fadeIn">
                                <div className="p-5 border rounded-lg shadow-2xl shadow-light-gray/20 bg-white/40 border-white/60">
                                    <div className="inline-flex items-center px-2 py-0.5 mb-2 text-14 font-text rounded-full bg-primary/10 text-primary">
                                        <MdCategory className="mr-1 size-3.5" />
                                        Therapeutic Classes
                                    </div>
                                    <h2 className="font-semibold text-20 text-secondary">Classifications & Indications</h2>
                                    <p className="mt-3 leading-relaxed text-secondary/80 text-16">
                                        {product.name} is classified under specific pharmaceutical categories and
                                        has designated indication codes.
                                    </p>
                                </div>

                                <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                        <h3 className="pl-2 mt-6 mb-3 font-medium border-l-2 text-secondary text-16 border-primary">Categories</h3>
                                        <div className="space-y-3">
                                            {product.categories.map((category, idx) => (
                                                <div key={idx} className="p-3 transition-all border rounded-md shadow-sm bg-white/40 border-white/60 hover:border-primary/30">
                                                    <div className="flex items-center">
                                                        <div className="flex items-center justify-center text-white rounded-md w-7 h-7 bg-primary">
                                                            <MdCategory className="size-4" />
                                                        </div>
                                                        <h4 className="ml-3 text-secondary text-16">{category}</h4>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="pl-2 mt-6 mb-3 font-medium border-l-2 text-secondary text-16 border-primary">Indication Codes</h3>
                                        <div className="space-y-3">
                                            {product.codes.map((code, idx) => (
                                                <div key={code._id} className="p-3 transition-all border rounded-md shadow-sm bg-white/40 border-white/60 hover:border-primary/30">
                                                    <div className="inline-flex items-center px-2 py-0.5 mb-1 text-14 font-text font-medium rounded-md text-primary bg-primary/10">
                                                        {code.type}
                                                    </div>
                                                    <h4 className="font-medium text-secondary text-16">{code.value}</h4>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <RelatedTradenames product={product} />

            <SponsersAffiliations hidePadding={true} />
        </PageLayout>
    );
}
